import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, useApi, type ApiClient, type Persona, type StaffDirectoryEntry } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";

type NamePair = { displayName: string; displayNameAr?: string | null; title?: string | null; titleAr?: string | null };

/**
 * Active staff from `GET /api/staff/directory` (security S1). A server from before the strict sign-in has only the persona list, which
 * stays as a deprecated, signed-in alias (SPEC C31) and is read only when the directory route is absent.
 */
async function staffDirectory(api: ApiClient): Promise<NamePair[]> {
  try {
    return await api.get<StaffDirectoryEntry[]>("/api/staff/directory");
  } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 404) throw e;
    return api.get<Persona[]>("/api/auth/staff/personas");
  }
}

/**
 * Staff names and job titles in the reading language. Server lines written before the Arabic name was carried ("اعتمد Mohammed …"),
 * and fields that hold only the English display name, read in Arabic from the staff directory.
 */
export function useStaffName(): (text: string | null | undefined) => string {
  const api = useApi();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const directory = useQuery({ queryKey: ["staff", "directory"], queryFn: () => staffDirectory(api), enabled: ar, staleTime: 10 * 60_000 });
  const pairs = useMemo(
    () => (directory.data ?? [])
      .flatMap((p) => [[p.displayName, p.displayNameAr], [p.title, p.titleAr]] as const)
      .filter((pair): pair is readonly [string, string] => !!pair[0] && !!pair[1] && pair[0] !== pair[1])
      .sort((a, b) => b[0].length - a[0].length),
    [directory.data],
  );
  return useCallback(
    (text) => {
      if (!text) return "";
      if (!ar || !pairs.length || !/[A-Za-z]/.test(text)) return text;
      let out = text;
      for (const [en, arName] of pairs) if (out.includes(en)) out = out.split(en).join(arName);
      return out;
    },
    [ar, pairs],
  );
}

/** Who took a recorded step, in the reading language: a staff member by name, an assistant or the platform by what it is. */
export function useActorName(): (e: { actorKind: string; actorName?: string | null; actorNameAr?: string | null }) => string {
  const staffName = useStaffName();
  const { t } = useTranslation();
  const { locale } = useLocale();
  return useCallback(
    (e) => {
      if (!e.actorName || e.actorName === "system" || e.actorKind === "System") return t("backoffice:stage.actor.System");
      if (e.actorKind === "Agent" && !e.actorNameAr) return staffName(e.actorName);
      return locale === "ar" && e.actorNameAr ? e.actorNameAr : staffName(e.actorName);
    },
    [staffName, t, locale],
  );
}
